import "./src/utility/disableAllCaching";
import React, { useState, useCallback, useEffect } from "react";
import { StatusBar } from "expo-status-bar";
import { StyleSheet, View } from "react-native";
import Route from "./src/navigation/Route";
import { useFonts } from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import "react-loading-skeleton/dist/skeleton.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AppColors } from "./src/utility/AppColors";
import { UserDataProvider } from "./src/context/UserDataContext";
import { EventSocketProvider } from "./src/context/EventSocketContext";
// import ElevenLabsWidget from "./src/Components/ElevenLabsWidget";
import { getLoginData } from "./src/utility/AppStoreData";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Cookies from "js-cookie";
import {
  Anybody_700Bold,
  Anybody_300Light,
  Anybody_500Medium,
  Anybody_400Regular,
  Anybody_800ExtraBold,
  Anybody_900Black,
  Anybody_500Medium_Italic,
  Anybody_600SemiBold,
  Anybody_400Regular_Italic,
  Anybody_600SemiBold_Italic,
} from "@expo-google-fonts/anybody";
import {
  Poppins_400Regular,
  Poppins_300Light,
} from "@expo-google-fonts/poppins";
import { injectGTMScript } from "./src/utility/gtm";

SplashScreen.preventAutoHideAsync();

/* 🧩 iOS Safari Image Cache Fix with Detailed Debug Logs */
if (
  typeof window !== "undefined" &&
  /iP(hone|od|ad)/i.test(navigator.userAgent)
) {
  console.log(
    "[iOS CacheFix] Detected iOS Safari. Applying cache prevention..."
  );

  // Meta headers to disable browser caching
  [
    "Cache-Control:no-store, no-cache, must-revalidate, max-age=0",
    "Pragma:no-cache",
    "Expires:0",
  ].forEach((rule) => {
    const [key, value] = rule.split(":");
    const meta = document.createElement("meta");
    meta.httpEquiv = key;
    meta.content = value;
    document.head.appendChild(meta);
  });
  console.log("[iOS CacheFix] Added meta headers to disable caching.");

  // CSS to force GPU redraw and prevent black rendering
  const style = document.createElement("style");
  style.textContent = `
    img {
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
      backface-visibility: hidden;
      will-change: transform;
    }
  `;
  document.head.appendChild(style);
  console.log("[iOS CacheFix] Applied GPU redraw CSS for <img> elements.");

  // Function to reload all images
  const reloadImages = () => {
    console.log(
      "[iOS CacheFix] 🔄 Reloading images due to back navigation or visibility change..."
    );
    const imgs = document.querySelectorAll("img");
    console.log(`[iOS CacheFix] Found ${imgs.length} images.`);

    imgs.forEach((img, index) => {
      try {
        const original = img.dataset.originalSrc || img.src;
        img.dataset.originalSrc = original;

        const url = new URL(original, window.location.href);
        const cacheBuster = Date.now();
        url.searchParams.set("nocache", cacheBuster);

        console.log(
          `[iOS CacheFix] [${index + 1}/${imgs.length}] Replacing:`,
          "\n   Original:",
          original,
          "\n   New:",
          url.toString()
        );

        img.src = url.toString();
        img.loading = "eager";
        img.crossOrigin = "anonymous";
      } catch (err) {
        console.error("[iOS CacheFix] ❌ Error reloading image:", img, err);
      }
    });

    console.log("[iOS CacheFix] ✅ Image reload complete.");
  };

  // Detect page restoration or visibility change
  window.addEventListener("pageshow", (e) => {
    console.log(
      "[iOS CacheFix] pageshow event fired. persisted =",
      e.persisted
    );
    if (e.persisted) {
      console.log(
        "[iOS CacheFix] Page restored from bfcache. Forcing reloadImages()."
      );
      reloadImages();
    }
  });

  document.addEventListener("visibilitychange", () => {
    console.log(
      "[iOS CacheFix] visibilitychange event:",
      document.visibilityState
    );
    if (document.visibilityState === "visible") {
      console.log(
        "[iOS CacheFix] Document became visible. Forcing reloadImages()."
      );
      reloadImages();
    }
  });

  console.log(
    "[iOS CacheFix] ✅ Setup complete. Waiting for navigation or visibility events..."
  );
}
/* 🧩 Toast Dark Theme Style */
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    .Toastify__toast--success,
    .Toastify__toast--error,
    .Toastify__toast--warning,
    .Toastify__toast--info {
      background-color: ${AppColors.black_121212} !important;
      color: ${AppColors.white} !important;
      border: 1px solid ${AppColors.buttonRed_DC2626} !important;
    }

    .Toastify__progress-bar {
      background: ${AppColors.appViolentColor_6D08C3} !important;
      height: 3px !important;
    }
  `;
  document.head.appendChild(style);
}

export default function App() {
  const [fontsLoaded] = useFonts({
    Anybody_700Bold,
    Anybody_300Light,
    Anybody_500Medium,
    Anybody_400Regular,
    Anybody_800ExtraBold,
    Anybody_900Black,
    Anybody_500Medium_Italic,
    Anybody_400Regular_Italic,
    Poppins_400Regular,
    Poppins_300Light,
    Anybody_600SemiBold,
    Anybody_600SemiBold_Italic,
  });

  const handleOnLayout = useCallback(async () => {
    if (fontsLoaded) await SplashScreen.hideAsync();
  }, [fontsLoaded]);

  useEffect(() => {
    injectGTMScript();
  }, []);

  if (!fontsLoaded) return null;

  return (
    <UserDataProvider>
      <EventSocketProvider>
        <View
          style={{ flex: 1, position: "relative" }}
          onLayout={handleOnLayout}
        >
          <StatusBar style="light" />
          <ToastContainer
            position="top-right"
            theme="dark"
            hideProgressBar={false}
            progressStyle={{
              backgroundColor: AppColors.appViolentColor_6D08C3,
              height: "3px",
            }}
            toastStyle={{
              backgroundColor: AppColors.black_121212,
              color: AppColors.white,
              borderRadius: 12,
              border: `1px solid ${AppColors.buttonRed_DC2626}`,
              fontFamily: "Anybody_500Medium",
            }}
            bodyStyle={{
              color: AppColors.white,
              fontFamily: "Anybody_500Medium",
              fontSize: 14,
            }}
            closeButtonStyle={{ color: AppColors.white }}
            autoClose={3000}
          />
          <Route />
          {/* <ElevenLabsWidget /> */}
        </View>
      </EventSocketProvider>
    </UserDataProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
});
